import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Badge, Spinner, Alert, ProgressBar } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileContract,
  faExclamationTriangle,
  faCheckCircle,
  faClock,
  faArrowRight,
  faHome,
  faHandshake,
  faKey,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import BrokerApiService from '../../services/brokerApiService';

interface Transaction {
  id: string;
  agent_id: string;
  agent_name: string;
  property_address: string;
  transaction_type: 'listing' | 'buying';
  stage: 'listing' | 'under_contract' | 'closing' | 'closed';
  compliance_status: 'compliant' | 'warning' | 'critical';
  document_count: number;
  total_documents: number;
  days_in_stage: number;
  last_activity: string;
  client_name: string;
}

interface TransactionStats {
  total_active: number;
  listing_count: number;
  under_contract_count: number;
  closing_count: number;
  compliance_alerts: number;
  recent_submissions: number;
  average_compliance_score: number;
}

interface ActiveTransactionsWidgetProps {
  brokerageId: string;
}

const ActiveTransactionsWidget: React.FC<ActiveTransactionsWidgetProps> = ({ brokerageId }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<TransactionStats>({
    total_active: 0,
    listing_count: 0,
    under_contract_count: 0,
    closing_count: 0,
    compliance_alerts: 0,
    recent_submissions: 0,
    average_compliance_score: 0
  });
  const [recentTransactions, setRecentTransactions] = useState<Transaction[]>([]);

  useEffect(() => {
    fetchTransactionData();
  }, [brokerageId]);

  const fetchTransactionData = async () => {
    try {
      setLoading(true);
      
      // Fetch real data from broker API
      const response = await BrokerApiService.getTransactions(brokerageId);
      
      if (response && response.success) {
        // Set stats from API response
        if (response.stats) {
          setStats({
            total_active: response.stats.total_active || 0,
            listing_count: response.stats.listing_count || 0,
            under_contract_count: response.stats.under_contract_count || 0,
            closing_count: response.stats.closing_count || 0,
            compliance_alerts: response.stats.compliance_alerts || 0,
            recent_submissions: response.stats.documents_needing_review || 0,
            average_compliance_score: response.stats.average_compliance_score || 0
          });
        }

        // Get transactions that need attention (warning or critical compliance)
        const needsAttention = response.transactions
          ?.filter((t: any) => ['warning', 'critical'].includes(t.compliance_status))
          ?.slice(0, 5) // Show top 5
          ?.map((t: any) => ({
            id: t.id,
            agent_id: t.agent_id,
            agent_name: t.agent_name,
            property_address: `${t.property_address || 'Property'}, ${t.property_city || 'City'}, ${t.property_state || 'State'}`,
            transaction_type: t.transaction_type || t.type || 'purchase',
            stage: t.stage,
            compliance_status: t.compliance_status,
            document_count: t.document_count || t.completed_documents || 0,
            total_documents: t.total_documents || 15,
            days_in_stage: parseInt(t.days_in_stage) || 0,
            last_activity: formatTimeAgo(new Date(t.last_activity_date || t.updated_at || t.created_at)),
            client_name: t.client_name || 'Unknown Client'
          })) || [];

        setRecentTransactions(needsAttention);
        setError(null);
      } else {
        // If no data, use empty state
        setStats({
          total_active: 0,
          listing_count: 0,
          under_contract_count: 0,
          closing_count: 0,
          compliance_alerts: 0,
          recent_submissions: 0,
          average_compliance_score: 0
        });
        setRecentTransactions([]);
      }
    } catch (err: any) {
      console.error('Error fetching transaction data:', err);
      
      // If the broker transactions endpoint doesn't exist yet, show empty state
      if (err.response?.status === 404) {
        setStats({
          total_active: 0,
          listing_count: 0,
          under_contract_count: 0,
          closing_count: 0,
          compliance_alerts: 0,
          recent_submissions: 0,
          average_compliance_score: 0
        });
        setRecentTransactions([]);
        setError(null); // Don't show error for missing endpoint
      } else {
        setError('Failed to load transaction data');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (minutes > 0) {
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else {
      return 'just now';
    }
  };

  const getStageIcon = (stage: string) => {
    switch (stage) {
      case 'listing':
        return faHome;
      case 'under_contract':
        return faHandshake;
      case 'closing':
        return faKey;
      case 'closed':
        return faCheck;
      default:
        return faFileContract;
    }
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'listing':
        return 'info';
      case 'under_contract':
        return 'warning';
      case 'closing':
        return 'primary';
      case 'closed':
        return 'success';
      default:
        return 'secondary';
    }
  };

  const getComplianceColor = (status: string) => {
    switch (status) {
      case 'compliant':
        return 'success';
      case 'warning':
        return 'warning';
      case 'critical':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getComplianceIcon = (status: string) => {
    switch (status) {
      case 'compliant':
        return faCheckCircle;
      case 'warning':
        return faExclamationTriangle;
      case 'critical':
        return faExclamationTriangle;
      default:
        return faClock;
    }
  };

  const handleViewAllTransactions = () => {
    navigate('/broker-intelligence/transactions');
  };

  const handleViewTransaction = (transactionId: string) => {
    navigate(`/broker-intelligence/transactions/${transactionId}`);
  };

  if (loading) {
    return (
      <Card className="h-100 ai-enhanced-card">
        <Card.Body className="d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
          <Spinner animation="border" variant="primary" />
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="h-100 ai-enhanced-card">
        <Card.Body>
          <Alert variant="danger">{error}</Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="h-100 ai-enhanced-card">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <FontAwesomeIcon icon={faFileContract} className="me-2 text-primary" />
          <h5 className="mb-0">Active Transactions</h5>
        </div>
        <Badge 
          bg="primary" 
          className="ai-hover-lift"
          style={{ cursor: 'pointer' }}
          onClick={handleViewAllTransactions}
        >
          View All <FontAwesomeIcon icon={faArrowRight} className="ms-1" />
        </Badge>
      </Card.Header>
      <Card.Body>
        {/* Summary Stats */}
        <Row className="mb-4">
          <Col xs={6} md={3}>
            <div className="text-center">
              <h3 className="mb-0 text-primary">{stats.total_active}</h3>
              <small className="text-muted">Active</small>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="text-center">
              <h3 className="mb-0 text-info">{stats.listing_count}</h3>
              <small className="text-muted">Listings</small>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="text-center">
              <h3 className="mb-0 text-warning">{stats.under_contract_count}</h3>
              <small className="text-muted">Contracts</small>
            </div>
          </Col>
          <Col xs={6} md={3}>
            <div className="text-center">
              <h3 className="mb-0 text-danger">{stats.compliance_alerts}</h3>
              <small className="text-muted">Alerts</small>
            </div>
          </Col>
        </Row>

        {/* Compliance Score */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-muted">Average Compliance Score</span>
            <strong className={`text-${stats.average_compliance_score >= 80 ? 'success' : stats.average_compliance_score >= 60 ? 'warning' : 'danger'}`}>
              {stats.average_compliance_score}%
            </strong>
          </div>
          <ProgressBar 
            now={stats.average_compliance_score} 
            variant={stats.average_compliance_score >= 80 ? 'success' : stats.average_compliance_score >= 60 ? 'warning' : 'danger'}
            style={{ height: '8px' }}
          />
        </div>

        {/* Recent Transactions with Issues */}
        <h6 className="mb-3">Needs Attention</h6>
        {recentTransactions.length === 0 ? (
          <div className="text-center text-muted py-3">
            <FontAwesomeIcon icon={faCheckCircle} size="2x" className="mb-2" />
            <p className="mb-0">All transactions are compliant!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {recentTransactions.map((transaction) => (
              <div 
                key={transaction.id}
                className="ai-hover-lift p-3 rounded border"
                style={{ cursor: 'pointer', borderColor: '#e0e0e0' }}
                onClick={() => handleViewTransaction(transaction.id)}
              >
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center mb-1">
                      <Badge bg={getStageColor(transaction.stage)} className="me-2">
                        <FontAwesomeIcon icon={getStageIcon(transaction.stage)} className="me-1" />
                        {transaction.stage.replace('_', ' ').toUpperCase()}
                      </Badge>
                      <Badge bg={getComplianceColor(transaction.compliance_status)} className="me-2">
                        <FontAwesomeIcon icon={getComplianceIcon(transaction.compliance_status)} className="me-1" />
                        {transaction.compliance_status.toUpperCase()}
                      </Badge>
                    </div>
                    <h6 className="mb-1">{transaction.property_address}</h6>
                    <div className="text-muted small">
                      <span className="me-3">Agent: {transaction.agent_name}</span>
                      <span>Client: {transaction.client_name}</span>
                    </div>
                  </div>
                  <div className="text-end">
                    <div className="fw-bold">{transaction.document_count}/{transaction.total_documents}</div>
                    <small className="text-muted">Docs</small>
                  </div>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    <FontAwesomeIcon icon={faClock} className="me-1" />
                    {transaction.days_in_stage} days in stage
                  </small>
                  <small className="text-muted">{transaction.last_activity}</small>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default ActiveTransactionsWidget;